<template>
  <div class="caringcm-institution">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__caringCmManagement") }}</h2>
      <div
        class="d-flex align-items-center justify-content-between mb-3 flex-wrap"
      >
        <form @submit.prevent="searchInstitution" class="me-auto me-2 mb-2">
          <div class="input-group rounded-pill">
            <input
              v-model="search"
              type="text"
              class="form-control rounded-pill"
              :placeholder="$t('__searchAgencyName')"
              aria-label="search"
              aria-describedby="search"
            />
            <button
              type="submit"
              class="btn btn-link px-2 text-dark"
              id="search"
            >
              <i class="bi bi-search"></i>
            </button>
            <button
              v-if="search"
              type="button"
              class="btn btn-primary px-3 ms-1 rounded-pill"
              id="clearSearch"
              @click="clearSearch"
            >
              {{ $t("__clearSearch") }}
            </button>
          </div>
        </form>
        <button
          v-if="hasPermission('caringcm.show')"
          @click="testConnection"
          class="mb-2 btn btn-outline-info rounded-pill me-2"
          :disabled="testing"
        >
          <span v-if="testing" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-wifi me-1"></i>
          {{ $t("__testConnection") }}
        </button>
        <button
          v-if="hasPermission('caringcm.create')"
          @click="showModal()"
          class="mb-2 fs-5 btn btn-primary px-2 rounded-circle lh-sm"
        >
          <i class="bi bi-plus"></i>
        </button>
      </div>
      <div class="card">
        <div v-if="items" class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="items"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>
            <template v-slot:cell(agency_name)="data">
              {{ data.item.agency && data.item.agency.name }}
            </template>
            <template v-slot:cell(username)="data">
              {{ data.item.username || '-' }}
            </template>
            <template v-slot:cell(has_password)="data">
              <span v-if="data.item.has_password" class="badge bg-success">{{ $t("__set") }}</span>
              <span v-else class="badge bg-secondary">{{ $t("__notSet") }}</span>
            </template>
            <template v-slot:cell(last_sync_at)="data">
              {{ data.item.last_sync_at ? formatDate(data.item.last_sync_at) : '-' }}
            </template>
            <template v-slot:cell(is_enabled)="data">
              <div
                class="status-switch"
                v-if="hasPermission('caringcm.update')"
              >
                <input
                  type="checkbox"
                  :id="'switch-' + data.index"
                  v-model="data.item.is_enabled"
                  @click="toggleAlert(data.item.id)"
                />
                <label :for="'switch-' + data.index"></label>
              </div>
              <span v-else>
                {{ data.item.is_enabled ? $t("__enabled") : $t("__disabled") }}
              </span>
            </template>
            <template v-slot:cell(active)="data">
              <div class="d-flex">
                <button
                  v-if="hasPermission('caringcm.update')"
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil mx-1 fs-5"></i>
                </button>
                <button
                  v-if="hasPermission('caringcm.delete') && !data.item.is_enabled"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                  @click="deleteAlert(data.item.id)"
                >
                  <i class="bi bi-trash fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row align-items-center mt-3" v-if="totalRows">
        <div class="col-sm col-12 text-right text-sm-left">
          {{
            $t("__paginationInfo", {
              startNub: currentPage == 1 ? 1 : perPage * (currentPage - 1) + 1,
              entNub:
                currentPage == 1
                  ? totalRows > perPage
                    ? perPage
                    : totalRows
                  : perPage * (currentPage - 1) + perPage > totalRows
                  ? totalRows
                  : perPage * (currentPage - 1) + perPage,
              sum: totalRows,
            })
          }}
        </div>
        <div class="col-sm col-12">
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            class="mb-sm-0 mb-3 mt-sm-0 mt-3"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 編輯/新增 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="editCaringCm" ref="editModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="submitForm">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ isEdit ? $t("__editCaringCm") : $t("__addCaringCm") }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t("__agencyName") }}</label>
                <v-select
                  v-model="modalData.agency_id"
                  :options="availableAgencies"
                  label="name"
                  :reduce="agency => agency.id"
                  :placeholder="$t('__openThisSelectMenu')"
                  :disabled="isEdit"
                  class="style-chooser"
                  :class="{ 'is-invalid': validate && validate.msg.agency_id }"
                  :components="{Deselect, OpenIndicator}"
                  :get-option-label="option => `${option.codename} / ${option.name}`"
                >
                </v-select>
                <div class="invalid-feedback" v-if="validate && validate.msg.agency_id">
                  <div v-for="(msg, index) in validate.msg.agency_id" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t("__caringCmUrl") }}</label>
                <input
                  v-model="modalData.base_url"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.base_url }"
                  placeholder="https://xxx.caringcm.com.tw"
                  required
                />
                <div class="invalid-feedback" v-if="validate && validate.msg.base_url">
                  <div v-for="(msg, index) in validate.msg.base_url" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t("__account") }}</label>
                <input
                  v-model="modalData.username"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.username }"
                  :required="!isEdit"
                />
                <div class="invalid-feedback" v-if="validate && validate.msg.username">
                  <div v-for="(msg, index) in validate.msg.username" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium" :class="{ required: !isEdit }">{{ $t("__password") }}</label>
                <input
                  v-model="modalData.password"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.password }"
                  :required="!isEdit"
                  :placeholder="isEdit ? $t('__leaveEmptyNoChange') : ''"
                />
                <div class="invalid-feedback" v-if="validate && validate.msg.password">
                  <div v-for="(msg, index) in validate.msg.password" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t("__nurseName") }}</label>
                <input
                  v-model="modalData.nurse_name"
                  type="text"
                  class="form-control"
                />
              </div>
              <div class="mb-3 d-flex align-items-center">
                <div class="status-switch me-2">
                  <input
                    type="checkbox"
                    id="isEnabled"
                    v-model="modalData.is_enabled"
                  />
                  <label for="isEnabled"></label>
                </div>
                <span>{{ $t("__enabled") }}</span>
              </div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t("__cancel") }}
              </button>
              <button type="submit" class="btn btn-primary rounded-pill">
                {{ $t("__save") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 刪除確認 Modal -->
    <div
      class="modal fade"
      id="deleteCaringCmModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="deleteModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__deleteCaringCm") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">
              {{ $t("__deleteCaringCmNote", { name: modalData.name }) }}
            </p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="deleteInstitution"
              type="button"
              class="btn btn-primary rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 切換狀態確認 Modal -->
    <div
      class="modal fade"
      id="toggleCaringCmModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="toggleModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__changeStatus") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">
              {{ $t("__changeStatusNote", { Agency: modalData.name }) }}
            </p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="toggleStatus"
              type="button"
              class="btn btn-primary rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip, Modal } from "bootstrap";
import i18n from "@/lang/lang.js";
import { caringcm } from "@/http/api/caringcm.js";
import { agency } from "@/http/api/agency.js";
import { mapState, mapGetters } from "vuex";
import moment from "moment-timezone";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "CaringCmInstitution",
  components: { vSelect },
  data() {
    return {
      Deselect: {
        render: createElement => createElement('span', ''),
      },
      OpenIndicator: {
        render: createElement => createElement('span', [
          createElement('img', {
            attrs: {
              src: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e"
            },
            style: { width: '16px', height: '16px' }
          })
        ]),
      },
      institutions: null,
      allAgencies: [],
      search: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      bootstrap_tooltip: null,
      bootstrap_modal: null,
      isEdit: false,
      editModalId: null,
      toggleId: null,
      modalData: {
        agency_id: null,
        institution_key: null,
        name: null,
        base_url: null,
        username: null,
        password: null,
        nurse_name: null,
        is_enabled: true,
      },
      validate: null,
      testing: false,
    };
  },
  computed: {
    ...mapState(["token"]),
    ...mapGetters(["hasPermission"]),
    fields() {
      return [
        {
          key: "name",
          label: i18n.t("__displayName"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "agency_name",
          label: i18n.t("__agencyName"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "base_url",
          label: i18n.t("__caringCmUrl"),
          class: "align-middle",
          sortable: false,
        },
        {
          key: "username",
          label: i18n.t("__account"),
          class: "align-middle",
          sortable: false,
        },
        {
          key: "has_password",
          label: i18n.t("__password"),
          class: "align-middle text-center",
          sortable: false,
        },
        {
          key: "last_sync_at",
          label: i18n.t("__lastSync"),
          class: "align-middle text-center",
          sortable: true,
        },
        {
          key: "is_enabled",
          label: i18n.t("__status"),
          class: "align-middle text-center",
          sortable: true,
        },
        {
          key: "active",
          label: i18n.t("__active"),
          tdClass: "align-middle",
          class: "align-middle",
          sortable: false,
        },
      ];
    },
    items() {
      if (!this.institutions) return null;
      let filtered = this.institutions;
      if (this.search) {
        const keyword = this.search.toLowerCase();
        filtered = filtered.filter((item) => {
          return (
            (item.name && item.name.toLowerCase().includes(keyword)) ||
            (item.institution_key && item.institution_key.toLowerCase().includes(keyword)) ||
            (item.agency && item.agency.name && item.agency.name.toLowerCase().includes(keyword))
          );
        });
      }
      return filtered;
    },
    availableAgencies() {
      if (!this.allAgencies) return [];
      const usedIds = (this.institutions || []).map((i) => i.agency_id);
      if (this.isEdit && this.modalData.agency_id) {
        return this.allAgencies.filter(
          (a) => !usedIds.includes(a.id) || a.id === this.modalData.agency_id
        );
      }
      return this.allAgencies.filter((a) => !usedIds.includes(a.id));
    },
  },
  watch: {
    items(data) {
      if (data) {
        this.initBootstrapTooltip();
        this.totalRows = data.length;
        this.currentPage = 1;
      }
    },
  },
  created() {
    this.getInstitutions();
    this.getAgencies();
  },
  methods: {
    getInstitutions() {
      caringcm.get(this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.institutions = res.data.data;
          this.totalRows = this.institutions.length;
        }
      });
    },
    getAgencies() {
      agency.get(null, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.allAgencies = res.data.data;
        }
      });
    },
    showModal(id) {
      this.validate = null;
      if (id) {
        this.isEdit = true;
        this.editModalId = id;
        caringcm.show(id, this.token).then((res) => {
          if (res && res.data && res.data.status === "success") {
            const d = res.data.data;
            this.modalData = {
              agency_id: d.agency_id,
              institution_key: d.institution_key,
              name: d.name,
              base_url: d.base_url,
              username: d.username,
              password: null,
              nurse_name: d.nurse_name,
              is_enabled: d.is_enabled,
            };
            this.bootstrap_modal = new Modal(this.$refs.editModal);
            this.bootstrap_modal.show();
          }
        });
      } else {
        this.isEdit = false;
        this.editModalId = null;
        this.modalData = {
          agency_id: null,
          institution_key: null,
          name: null,
          base_url: null,
          username: null,
          password: null,
          nurse_name: null,
          is_enabled: true,
        };
        this.bootstrap_modal = new Modal(this.$refs.editModal);
        this.bootstrap_modal.show();
      }
    },
    submitForm() {
      const data = { ...this.modalData };
      if (this.isEdit && !data.password) {
        delete data.password;
      }

      const request = this.isEdit
        ? caringcm.update(this.editModalId, data, this.token)
        : caringcm.create(data, this.token);

      request
        .then((res) => {
          if (res && res.data && res.data.status === "success") {
            this.bootstrap_modal.hide();
            this.getInstitutions();
          }
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            this.validate = {
              msg: err.response.data.validate || {},
            };
          }
        });
    },
    toggleAlert(id) {
      this.toggleId = id;
      const inst = this.institutions.find((i) => i.id === id);
      if (inst) {
        this.modalData.name = inst.name;
      }
      this.bootstrap_modal = new Modal(this.$refs.toggleModal);
      this.bootstrap_modal.show();
    },
    toggleStatus() {
      caringcm.toggle(this.toggleId, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.bootstrap_modal.hide();
          this.getInstitutions();
        }
      });
    },
    deleteAlert(id) {
      this.editModalId = id;
      const inst = this.institutions.find((i) => i.id === id);
      if (inst) {
        this.modalData.name = inst.name;
      }
      this.bootstrap_modal = new Modal(this.$refs.deleteModal);
      this.bootstrap_modal.show();
    },
    deleteInstitution() {
      caringcm.delete(this.editModalId, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.bootstrap_modal.hide();
          this.getInstitutions();
        }
      });
    },
    testConnection() {
      this.testing = true;
      caringcm
        .testConnection(this.token)
        .then((res) => {
          if (res && res.data && res.data.data && res.data.data.healthy) {
            alert(i18n.t("__connectionSuccess"));
          } else {
            alert(i18n.t("__connectionFailed"));
          }
        })
        .catch(() => {
          alert(i18n.t("__connectionFailed"));
        })
        .finally(() => {
          this.testing = false;
        });
    },
    searchInstitution() {
      // search 是 reactive，items computed 會自動過濾
    },
    clearSearch() {
      this.search = null;
    },
    formatDate(dateStr) {
      return moment(dateStr).format("YYYY-MM-DD HH:mm");
    },
    initBootstrapTooltip() {
      this.$nextTick(() => {
        const tooltipTriggerList = [].slice.call(
          document.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipTriggerList.map(
          (tooltipTriggerEl) => new Tooltip(tooltipTriggerEl)
        );
      });
    },
  },
};
</script>
